// ============================================================================
// @gs/feedback/schemas — vocabulaires + schemas Zod partages client/serveur
// ============================================================================
// SPEC-FEEDBACK-TRANSVERSE-2026-06-10 (validee David « go les 6 »). Les codes
// sont EN stricts (transcodages annexe A, docs/migration/inventaire/03) — les
// libelles FR/EN vivent dans i18n (namespace `feedback`), JAMAIS ici.
// Miroir exact des CHECK de la migration 116 (CK_fbk_Tickets_*, CK_fbk_Messages_kind).

import { z } from 'zod';

// ── Vocabulaires (miroir CHECK mig 116) ─────────────────────────────────────

export const FEEDBACK_TYPES = ['BUG', 'FEATURE', 'IMPROVEMENT', 'GENERAL'] as const;
export type FeedbackType = (typeof FEEDBACK_TYPES)[number];

export const FEEDBACK_STATUSES = [
  'NEW',
  'IN_PROGRESS',
  'WAITING',
  'TO_VALIDATE',
  'RESOLVED',
  'CANCELLED',
  'ARCHIVED',
] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

/**
 * Statut « archive » (terminal) — CHORE-FBK-LIFECYCLE. L'archivage REUTILISE ce
 * statut existant (deja livre par la mig 116, CHECK CK_fbk_Tickets_status, chips
 * et i18n) : AUCUN flag distinct, AUCUNE migration. Un ticket ARCHIVED sort de la
 * vue active par defaut (filterTickets) ; la facette Statut le ramene.
 */
export const ARCHIVED_STATUS: FeedbackStatus = 'ARCHIVED';

/**
 * Cible de « Rouvrir » — un ticket sorti de l'archive revient dans la file active
 * en « En cours » (geste = on s'en occupe a nouveau). Choix FIXE et identique
 * depuis le detail ET la liste (coherence) ; la trace complete reste dans
 * l'historique (StatusHistory + Activity). Reversibilite = on quitte ARCHIVED,
 * pas la restauration de l'exact statut d'avant (out of scope, aucune colonne).
 */
export const REOPEN_STATUS: FeedbackStatus = 'IN_PROGRESS';

/**
 * Prochain statut d'une bascule archiver/rouvrir (helper PUR, teste sans DOM —
 * CHORE-FBK-LIFECYCLE) : un ticket actif part vers ARCHIVED, un ticket archive
 * revient vers REOPEN_STATUS. Source unique partagee par la liste ET le detail
 * (zero divergence de comportement).
 */
export function nextLifecycleStatus(current: FeedbackStatus | string): FeedbackStatus {
  return current === ARCHIVED_STATUS ? REOPEN_STATUS : ARCHIVED_STATUS;
}

export const FEEDBACK_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
export type FeedbackPriority = (typeof FEEDBACK_PRIORITIES)[number];

export const MESSAGE_KINDS = ['MESSAGE', 'STATUS_CHANGE'] as const;
export type MessageKind = (typeof MESSAGE_KINDS)[number];

/** Reactions emoji v1 (parite POC — 6 emojis fixes, decision §8.e). */
export const REACTION_EMOJIS = ['👍', '❤️', '🎉', '✅', '👀', '🙏'] as const;

// ── Limites (parite POC, appliquees CLIENT ET SERVEUR) ──────────────────────

export const TITLE_MAX = 120;
export const BODY_MAX = 4000;
export const MAX_ATTACHMENTS = 3;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10 Mo
export const ATTACHMENT_CONTENT_TYPES = /^(image\/|application\/pdf$)/;
/** Fenetre d'edition/suppression douce d'un message — verifiee SERVEUR (spec §4). */
export const EDIT_WINDOW_MINUTES = 15;

// ── Diagnostics (capture navigateur auto — G40/G42 : Zod ecriture ET lecture) ─

export const diagnosticsSchema = z.object({
  userAgent: z.string().max(400),
  viewport: z.string().max(40), // "1920x1080"
  url: z.string().max(2000),
  locale: z.string().max(20),
  platform: z.string().max(100).optional(),
  /** Ring buffer console (200 entrees max, pattern POC consoleCapture). */
  logs: z.array(z.string().max(2000)).max(200).optional(),
});
export type FeedbackDiagnostics = z.infer<typeof diagnosticsSchema>;

// ── Corps de requetes ────────────────────────────────────────────────────────

export const createTicketSchema = z.object({
  type: z.enum(FEEDBACK_TYPES),
  title: z.string().trim().min(1).max(TITLE_MAX),
  description: z.string().trim().min(1).max(BODY_MAX),
  diagnostics: diagnosticsSchema.optional(),
});
export type CreateTicketInput = z.infer<typeof createTicketSchema>;

export const createMessageSchema = z.object({
  body: z.string().trim().min(1).max(BODY_MAX),
  /** Adjacency threading profondeur 1 (legacy — le composer ne l'emet plus). */
  parentMessageId: z.string().guid().optional(),
  /** Quote-reply plat (CHORE-FBK-COMPOSER-V2, mig 120) : message CITE. Valide
   *  SERVEUR (meme ticket, kind MESSAGE, non supprime) ; ignore tant que la
   *  colonne n'est pas appliquee (capability-aware). */
  replyToId: z.string().guid().optional(),
  clientMsgId: z.string().max(64).optional(),
});
export type CreateMessageInput = z.infer<typeof createMessageSchema>;

/** Longueur max de l'extrait de citation (resolu SERVEUR, assaini). */
export const QUOTE_SNIPPET_MAX = 120;

/**
 * Extrait de citation borne et assaini (helper PUR, teste sans DOM) : espaces
 * blancs aplatis (un CR/LF dans la citation casserait la mise en page du bloc),
 * tronque a QUOTE_SNIPPET_MAX avec ellipse.
 */
export function quoteSnippet(body: string): string {
  const flat = body.replace(/\s+/g, ' ').trim();
  return flat.length <= QUOTE_SNIPPET_MAX ? flat : `${flat.slice(0, QUOTE_SNIPPET_MAX - 1).trimEnd()}…`;
}

export const editMessageSchema = z.object({
  body: z.string().trim().min(1).max(BODY_MAX),
});

export const toggleReactionSchema = z.object({
  emoji: z.string().min(1).max(16),
  active: z.boolean(),
});

export const triageSchema = z
  .object({
    status: z.enum(FEEDBACK_STATUSES).optional(),
    priority: z.enum(FEEDBACK_PRIORITIES).optional(),
    adminNotes: z.string().max(BODY_MAX).nullable().optional(),
  })
  .refine((p) => p.status !== undefined || p.priority !== undefined || p.adminNotes !== undefined, {
    message: 'Patch vide',
  });
export type TriageInput = z.infer<typeof triageSchema>;

export const createNoteSchema = z.object({
  body: z.string().trim().min(1).max(BODY_MAX),
});

// Tout marquer comme lu (CHORE-FBK-INBOX-V2) : portee = les tickets ACTUELLEMENT
// filtres/affiches cote client (la liste est bornee a 200 — le cap suit).
export const markReadBatchSchema = z.object({
  ticketIds: z.array(z.string().guid()).min(1).max(200),
});
export type MarkReadBatchInput = z.infer<typeof markReadBatchSchema>;

// ── DTOs (snake_case SQL -> camelCase DANS LE SERVICE, jamais expose brut) ───

export interface TicketDto {
  id: string;
  ticketNumber: string;
  originBcKey: string;
  type: FeedbackType;
  status: FeedbackStatus;
  priority: FeedbackPriority;
  title: string;
  description: string;
  createdByUserId: string;
  createdByName: string;
  createdAt: string;
  lastActivityAt: string;
  lastActivityBy: string | null;
  adminNotes: string | null;
  diagnostics: FeedbackDiagnostics | null;
  isFollowing: boolean;
  /** Non-lu pour le LECTEUR courant (marqueur fbk.Reads PAR ticket/lecteur) —
   *  c'est le `hasUnread` de l'inbox (CHORE-FBK-UNREAD-INBOX). */
  unread: boolean;
  /** Messages visibles non vus par le lecteur (extension ADDITIVE,
   *  CHORE-FBK-UNREAD-INBOX) — 0 possible avec unread=true (ticket frais sans
   *  message, activite de triage). Notes internes exclues par construction. */
  unreadCount: number;
  /** Marqueur « lu jusqu'a » du LECTEUR courant (fbk.Reads.last_seen_at, ISO —
   *  extension ADDITIVE CHORE-FBK-INBOX-V2). null = jamais lu OU non-lu manuel
   *  (marqueur desactive). Sert au separateur « Nouveaux messages » du fil ;
   *  donnee PAR lecteur, jamais partagee. */
  lastSeenAt: string | null;
  /** Provenance (chip corporate, CHORE-FIX-B1 P2 — extension ADDITIVE) :
   *  renseignes UNIQUEMENT pour un lecteur corporate quand le ticket vient d'une
   *  AUTRE compagnie que la sienne ; null pour tous les autres lecteurs. */
  companyCode: string | null;
  companyName: string | null;
}

export interface MessageDto {
  id: string;
  authorUserId: string;
  authorName: string;
  authorRole: string | null;
  body: string;
  kind: MessageKind;
  parentMessageId: string | null;
  /** Quote-reply (CHORE-FBK-COMPOSER-V2, mig 120 — extension ADDITIVE) : message
   *  CITE resolu SERVEUR. `snippet` null = message cite supprime depuis (le
   *  client affiche son libelle « supprime »). null/absent = pas une citation
   *  OU colonne pas encore appliquee (capability-aware). */
  replyTo?: { id: string; authorName: string; snippet: string | null } | null;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
  clientMsgId: string | null;
  reactions: Array<{ emoji: string; count: number; mine: boolean }>;
}

export interface InternalNoteDto {
  id: string;
  authorUserId: string;
  authorName: string;
  body: string;
  createdAt: string;
}

export interface ActivityDto {
  id: string;
  action: string;
  byUserId: string;
  byName: string;
  byRole: string | null;
  at: string;
  detail: string | null;
  refMessageId: string | null;
}

export interface NotificationDto {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  refType: string | null;
  refId: string | null;
  createdAt: string;
  read: boolean;
}

/** Formate FB-YYYY-NNN (pad 3 — depasse proprement 999 : FB-2026-1000). */
export function formatTicketNumber(year: number, value: number): string {
  return `FB-${year}-${String(value).padStart(3, '0')}`;
}
